import type { ExtendedMenuItem } from '@shared/types/menu';

type HasPermissionFn = (permission: string) => boolean;
type HasRoleFn = (role: string) => boolean;

function hasChildrenProp(item: unknown): item is { children: ExtendedMenuItem[] } {
  return (
    typeof item === 'object' &&
    item !== null &&
    'children' in item &&
    Array.isArray((item as Record<string, unknown>).children)
  );
}

/** Пункт несёт собственный гейт — правом или ролью. */
function isGated(item: ExtendedMenuItem): boolean {
  return Boolean(item.requiredPermission) || Boolean(item.requiredAnyPermissions?.length) || Boolean(item.requiredRole);
}

/**
 * Фильтрует меню по правам и ролям пользователя.
 *
 * `hasRole` необязателен для обратной совместимости с вызовами, которым роли не нужны.
 * Пункт с `requiredRole` без переданного `hasRole` СКРЫВАЕТСЯ (fail-closed): показать
 * пункт, ведущий на роут с ролевым гейтом, значит отправить оператора в молчаливый
 * редирект на дашборд.
 */
export function filterMenuByPermissions(
  items: ExtendedMenuItem[],
  hasPermission: HasPermissionFn,
  hasRole?: HasRoleFn,
): ExtendedMenuItem[] {
  const result: ExtendedMenuItem[] = [];

  for (const item of items) {
    const perm = item.requiredPermission;
    if (perm && !hasPermission(perm)) continue;

    const anyPermissions = item.requiredAnyPermissions;
    if (anyPermissions?.length && !anyPermissions.some(hasPermission)) continue;

    const role = item.requiredRole;
    if (role && !(hasRole ? hasRole(role) : false)) continue;

    if (hasChildrenProp(item) && item.children.length > 0) {
      const filteredChildren = filterMenuByPermissions(item.children, hasPermission, hasRole);
      if (filteredChildren.length === 0) continue;
      // Группа без собственного права: её открывает не наличие детей вообще, а наличие
      // хотя бы одного ПРОШЕДШЕГО гейт ребёнка. Негейченные дети (хаб, заглушки,
      // Swagger, «Скоро») группу не открывают — иначе она была бы видна всем.
      // Смотрим только прямых детей: «виден хотя бы один дочерний пункт» — про них.
      if (item.requiresGatedChild && !filteredChildren.some(isGated)) continue;
      result.push({ ...item, children: filteredChildren } as ExtendedMenuItem);
    } else {
      result.push(item);
    }
  }

  return result;
}
